import React, {useEffect, useState} from 'react';
import { useNavigate } from 'react-router-dom';
import ErrorBoundary from '../components/ErrorBoundary.jsx';
import { Shell } from '../components/Shell';
import Loader from '../components/Loader';
import { runOcr } from '../services/ocr';
import { UploadModal } from '../components/UploadModal';
import { fetchCoverage, buildCoverageChips, nextStepFor } from '../components/EntryFlowModal';
import { MonthLedger } from '../components/MonthLedger';
import { X } from '../components/v2/Icons';
import { EntryStepper } from '../components/v2/EntryStepper';
import { CategoryContext } from '../components/v2/CategoryContext';
import { PageHeader, Card, Button, Field, Input, Select, UnitInput, Callout, Skeleton, Modal } from '../components/v2/ui';
import { useT } from '../i18n';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5050';
const getCurrentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

export const InvoicesPage = () => {
  const navigate = useNavigate();
  const { t } = useT();
  const [file, setFile] = useState(null);
  const [ocrLoading, setOcrLoading] = useState(false);
  const [ocrMetadata, setOcrMetadata] = useState(null);
  const [showOcrFailureModal, setShowOcrFailureModal] = useState(false);
  const [carbonData, setCarbonData] = useState({
    totalEmissions: 0,
    totalKwh: 0,
    monthlyData: [],
    registrations: []
  });
  const [manualInputs, setManualInputs] = useState({
    electricity: '',
    billingPeriod: getCurrentMonth()
  });
  const [buildings, setBuildings] = useState([]);
  const [selectedBuilding, setSelectedBuilding] = useState('');
  const [buildingValidationError, setBuildingValidationError] = useState('');
  const [saving, setSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const [dataEntryMethod, setDataEntryMethod] = useState('invoice'); // 'manual' or 'invoice'
  const [consumptionPurpose, setConsumptionPurpose] = useState('building'); // 'building', 'ev', 'hybrid'
  // Verifiko → Ruaj → Vazhdo dialog
  // Zgjidh vendin → Shto të dhënat → Rishiko & regjistro
  const [step, setStep] = useState(0);
  const [flowError, setFlowError] = useState('');
  const [coverage, setCoverage] = useState(null);
  const [ledgerKey, setLedgerKey] = useState(0);
  const [selectedVehicle, setSelectedVehicle] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0, processing: false });
  const bulkRef = React.useRef(false); // suppress the verify dialog during bulk scans
  const [, setSummary] = useState({ current: null, previous: null });

  // Fetch summary data for bar chart comparison
  const fetchSummary = async (period) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/emissions/scope2/electricity/summary?period=${period}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSummary(data);
      }
    } catch (err) {
      console.error('Failed to fetch summary:', err);
    }
  };

  // Fetch summary when billing period changes
  useEffect(() => {
    if (manualInputs.billingPeriod) {
      fetchSummary(manualInputs.billingPeriod);
    }
  }, [manualInputs.billingPeriod]);

  // Fetch buildings on mount
  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch(`${API_BASE}/api/buildings`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
      .then(res => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then(data => setBuildings(Array.isArray(data) ? data : []))
      .catch(err => {
        console.error('Failed to fetch buildings:', err);
        setBuildings([]);
      });
  }, []);

  // Calculate carbon emissions from consumption data
  const calculateCarbonEmissions = (consumptionTable) => {
    if (!consumptionTable || consumptionTable.length === 0) return null;
    
    const EMISSION_FACTOR = 1.34; // kg CO2e per kWh — Kosovo provisional national grid factor (World Bank / Grutter Consulting 2016)
    
    let totalKwh = 0;
    const monthlyData = [];
    const registrations = [];
    
    consumptionTable.forEach(row => {
      const kwh = row.consumption || row.monthlyConsumption || 0;
      const label = row.label || row.line || '';
      const amount = row.amount || row.amountEur || 0;
      
      totalKwh += kwh;
      
      monthlyData.push({
        line: label,
        kwh: kwh,
        emissions: kwh * EMISSION_FACTOR,
        amount: amount,
        euroCoefficient: 1.0,
        price: row.price || 0
      });
      
      registrations.push({
        type: label.split('/')[0] || 'A1',
        value: kwh,
        unit: 'kWh'
      });
    });
    
    return {
      totalEmissions: totalKwh * EMISSION_FACTOR,
      totalKwh: totalKwh,
      monthlyData: monthlyData,
      registrations: registrations
    };
  };

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if(f) {
      setFile(f);
      setTimeout(() => runOcrOnFile(f), 500);
    }
  };

  const runOcrOnFile = async (file) => {
    if (!selectedBuilding) {
      return;
    }
    
    setOcrLoading(true);
    setIsSaved(false); // Reset save state so user can re-save after new upload
    try {
      // Tag uploads from this page as Scope 2 electricity so they appear under the correct category in Reports/Recipes.
      const result = await runOcr(file, selectedBuilding, 'scope2-elektriciteti');
      setOcrMetadata(result);
      const rows = Array.isArray(result?.consumptionTable) ? result.consumptionTable : [];
      
      const avgConfidence = rows.length > 0 
        ? rows.reduce((sum, row) => sum + (row.confidence || 0), 0) / rows.length 
        : 0;
      const hasData = rows.length > 0 && rows.some(row => (row.consumption || row.monthlyConsumption || 0) > 0);
      
      if (!hasData || rows.length === 0) {
        setShowOcrFailureModal(true);
      }
      
      if (rows && rows.length > 0) {
        const totalKwh = rows.reduce((sum, row) => sum + (row.consumption || row.monthlyConsumption || 0), 0);
        // Use the period read from the bill itself — saving a May bill under the
        // current month would double-count it (backend records it under May)
        const ocrPeriod = /^\d{4}-\d{2}$/.test(result.extractedPeriod || '') ? result.extractedPeriod : null;
        setManualInputs(prev => ({
          ...prev,
          electricity: totalKwh.toString(),
          billingPeriod: ocrPeriod || prev.billingPeriod || getCurrentMonth()
        }));
        // OCR data is now auto-saved to Scope2ProcessRecord on the backend.
        // "Ruaj" button stays enabled but backend duplicate guard (409) prevents double-save.
        
        // Use emissions data from backend if available and valid, otherwise calculate locally
        const useBackendEmissions = result.emissions && result.emissions.totalConsumption > 0;
        
        if (useBackendEmissions) {
          setCarbonData({
            totalEmissions: result.emissions.emissionsKg || 0,
            totalKwh: result.emissions.totalConsumption || 0,
            monthlyData: rows.map(row => ({
              line: row.label || row.line || '',
              kwh: row.consumption || row.monthlyConsumption || 0,
              emissions: (row.consumption || row.monthlyConsumption || 0) * (result.emissions.emissionFactor || 0.35),
              amount: row.amount || 0,
              euroCoefficient: (row.label || '').includes('B1') ? 1.35 : 1.0,
              price: row.price || 0
            })),
            registrations: rows.map(row => ({
              type: (row.label || row.line || 'A1').split('/')[0],
              value: row.consumption || row.monthlyConsumption || 0,
              unit: 'kWh'
            }))
          });
        } else {
          // Fallback to local calculation
          const EMISSION_FACTOR = 1.34; // kg CO2e per kWh — Kosovo provisional national grid factor (World Bank / Grutter Consulting 2016)
          const totalEmissionsKg = totalKwh * EMISSION_FACTOR;
          
          setCarbonData({
            totalEmissions: totalEmissionsKg,
            totalKwh: totalKwh,
            monthlyData: rows.map(row => {
              const kwh = row.consumption || row.monthlyConsumption || 0;
              return {
                line: row.label || row.line || '',
                kwh: kwh,
                emissions: kwh * EMISSION_FACTOR,
                amount: row.amount || 0,
                euroCoefficient: (row.label || '').includes('B1') ? 1.35 : 1.0,
                price: row.price || 0
              };
            }),
            registrations: rows.map(row => ({
              type: (row.label || row.line || 'A1').split('/')[0],
              value: row.consumption || row.monthlyConsumption || 0,
              unit: 'kWh'
            }))
          });
        }

        // Verifiko → Ruaj → Vazhdo: show the scanned bill + numbers right away.
        // Skipped during bulk upload — each next scan would clobber the dialog
        // mid-verification; bulk records are auto-saved by the backend.
        if (!bulkRef.current) {
          setFlowError('');
          setStep(2);
        }
      }

    } catch (err) {
      console.error('OCR failed:', err);
      setShowOcrFailureModal(true);
      setCarbonData({ totalEmissions: 0, totalKwh: 0, monthlyData: [], registrations: [] });
    }
    setOcrLoading(false);
  };

  // Handle single file upload from modal
  const handleSingleUpload = (file) => {
    if (!selectedBuilding) {
      setBuildingValidationError(t('invp.pickBuildingBeforeInvoice'));
      return;
    }
    setFile(file);
    runOcrOnFile(file);
  };

  // Handle bulk file upload from modal - process sequentially
  const handleBulkUpload = async (files) => {
    if (!selectedBuilding) {
      setBuildingValidationError(t('invp.pickBuildingBeforeInvoices'));
      return;
    }
    
    setBulkProgress({ current: 0, total: files.length, processing: true });
    bulkRef.current = true;

    for (let i = 0; i < files.length; i++) {
      setBulkProgress(prev => ({ ...prev, current: i + 1 }));
      setFile(files[i]);
      await runOcrOnFile(files[i]);
      
      // Small delay between files to avoid overwhelming the server
      if (i < files.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }
    
    bulkRef.current = false;
    setBulkProgress({ current: 0, total: 0, processing: false });
  };

  const handleManualInputChange = (field, value) => {
    setManualInputs(prev => ({
      ...prev,
      [field]: value
    }));
    setIsSaved(false);
    setBuildingValidationError('');
  };

  const calculateManual = () => {
    const electricity = parseFloat(manualInputs.electricity) || 0;
    const EMISSION_FACTOR = 1.34; // kg CO2e per kWh — Kosovo provisional national grid factor (World Bank / Grutter Consulting 2016)
    
    const emissions = electricity * EMISSION_FACTOR;
    
    setCarbonData({
      totalEmissions: emissions,
      totalKwh: electricity,
      monthlyData: [{
        line: 'Manual Input',
        kwh: electricity,
        emissions: emissions,
        amount: 0,
        euroCoefficient: 1.0
      }],
      registrations: [{
        type: 'Manual',
        value: electricity,
        unit: 'kWh'
      }]
    });
    setIsSaved(false);
    setShowOcrFailureModal(false);
    setBuildingValidationError('');
  };

  const handleBuildingConfirm = () => {
    if (!selectedBuilding) {
      setBuildingValidationError(t('invp.pickBuilding'));
      return;
    }
    setBuildingValidationError('');
  };

  const handleSave = async () => {
    const electricityValue = parseFloat(manualInputs.electricity) || carbonData.totalKwh || 0;
    if (!selectedBuilding) {
      setBuildingValidationError(t('elec.errBuilding'));
      return;
    }
    if (!manualInputs.billingPeriod) {
      setBuildingValidationError(t('elec.errPeriod'));
      return;
    }
    if (!electricityValue || electricityValue <= 0 || !carbonData.totalEmissions) {
      setBuildingValidationError(t('elec.errCalc'));
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      if (!token) {
        alert(t('invp.sessionExpired'));
        window.location.href = '/';
        return;
      }

      const payload = {
        buildingId: parseInt(selectedBuilding),
        processType: consumptionPurpose === 'building' ? 'electricity' : 'ev_charging',
        /* electricity_grid, NOT grid_electricity. The reversed key has no matching
           row in the emission_factors registry, so the Phase 0 verifier could not
           recompute the save: every electricity record written since that work
           landed with factor_key NULL and calc_check 'unverified'. The migration
           fix-scope2-fuel-key-typo.sql repaired the 22 historical rows but the
           client kept sending the reversed key, so the defect regenerated on
           every save. Confirmed against production 2026-07-30. */
        fuelKey: 'electricity_grid',
        period: manualInputs.billingPeriod,
        quantity: electricityValue,
        unit: 'kWh',
        energy_kWh: carbonData.totalKwh,
        emissions_tCO2e: parseFloat(((carbonData.totalEmissions || 0) / 1000).toFixed(6)),
        consumptionPurpose: consumptionPurpose,
        /* The API reads req.body.activityPurpose for the activity_purpose column;
           consumptionPurpose alone was silently dropped. */
        activityPurpose: consumptionPurpose === 'building' ? 'Elektriciteti i ndërtesës'
          : consumptionPurpose === 'ev' ? 'Karikimi EV' : 'Karikimi hibrid',
        vehicleId: (consumptionPurpose === 'ev' || consumptionPurpose === 'hybrid') ? selectedVehicle : null
      };

      const response = await fetch(`${API_BASE}/api/emissions/scope2/process/records`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.status === 401) {
        alert(t('invp.sessionExpired'));
        localStorage.removeItem('token');
        window.location.href = '/';
        return;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Save failed');
      }

      setIsSaved(true);
      setBuildingValidationError('');
      return true;
    } catch (err) {
      console.error('Save error:', err);
      setBuildingValidationError(t('invp.saveError', { message: err.message }));
      return false;
    } finally {
      setSaving(false);
    }
  };

  // "Ruaj" on the page opens the verification dialog; the dialog saves.
  /**
   * Step 2's action: calculate, then advance to review.
   *
   * The old screen had a separate "Kalkulo manualisht" button, because
   * handleManualInputChange only stores the input — calculateManual() is what
   * produces carbonData. The stepper has one action per step, so this does both.
   *
   * It validates the ENTERED kWh rather than carbonData.totalEmissions: on the
   * manual path the emissions do not exist yet at this point, and requiring them
   * here would reject every manual entry.
   */
  const handleOpenVerify = () => {
    const electricityValue = parseFloat(manualInputs.electricity) || carbonData.totalKwh || 0;
    if (!selectedBuilding) {
      setBuildingValidationError(t('elec.errBuilding'));
      setStep(0);
      return;
    }
    if (!manualInputs.billingPeriod) {
      setBuildingValidationError(t('elec.errPeriod'));
      return;
    }
    if (!electricityValue || electricityValue <= 0) {
      setBuildingValidationError(t('elec.errCalc'));
      return;
    }
    // OCR has already filled carbonData on the invoice path.
    if (dataEntryMethod !== 'invoice') calculateManual();
    setBuildingValidationError('');
    setFlowError('');
    setStep(2);
  };

  const handleFlowSave = async () => {
    setFlowError('');
    const ok = await handleSave();
    if (!ok) {
      setFlowError(t('invp.saveRetry'));
      return;
    }
    try {
      setCoverage(await fetchCoverage(manualInputs.billingPeriod));
    } catch (e) {
      setCoverage(null);
    }
    setLedgerKey(k => k + 1);
  };

  const resetEntry = () => {
    setFile(null);
    setOcrMetadata(null);
    setIsSaved(false);
    setCarbonData({ totalEmissions: 0, totalKwh: 0, monthlyData: [], registrations: [] });
    setManualInputs(prev => ({ ...prev, electricity: '' }));
  };

  const handleFlowAddAnother = () => { resetEntry(); setStep(1); };
  const handleFlowFinish = () => { resetEntry(); setStep(0); };

  const [flowDocUrl, setFlowDocUrl] = useState(null);
  useEffect(() => {
    if (!file) { setFlowDocUrl(null); return undefined; }
    const url = URL.createObjectURL(file);
    setFlowDocUrl(url);
    return () => URL.revokeObjectURL(url); // avoid leaking blob URLs across scans
  }, [file]);

  /* Gate on what the user has ENTERED, not on carbonData: on the manual path
     carbonData.totalKwh is 0 until calculateManual() runs, and that now runs from
     this very button — gating on it would leave the button permanently disabled. */
  const enteredKwh = parseFloat(manualInputs.electricity) || carbonData.totalKwh || 0;
  const isSaveDisabled = saving || isSaved || !selectedBuilding || !manualInputs.billingPeriod || enteredKwh <= 0;

  const totalKwhValue = parseFloat(manualInputs.electricity) || carbonData.totalKwh || 0;
  const tCO2e = ((carbonData.totalEmissions || 0) / 1000).toFixed(3);
  const purposeLabel = t(
    consumptionPurpose === 'building' ? 'elec.purposeBuilding'
      : consumptionPurpose === 'ev' ? 'elec.purposeEv' : 'elec.purposeHybrid'
  );

  const page = (
    <Shell>
      <div className="k2-wrap">
        {(ocrLoading || saving) && <Loader />}

        <PageHeader title={t('elec.title')} desc={t('elec.desc')}>
          <Button variant="gh" icon={X.Doc} onClick={() => navigate('/records')}>
            {t('entry.viewRecords')}
          </Button>
        </PageHeader>

        <EntryStepper
          scope={2}
          step={step}
          setStep={setStep}
          facilities={buildings}
          siteId={selectedBuilding}
          onSite={(e) => { setSelectedBuilding(e.target.value); setBuildingValidationError(''); }}
          siteError={buildingValidationError}
          /* Consumption purpose is a real field here — scope2_process_records has an
             activity_purpose column, and it also drives whether a vehicle is asked for. */
          purposes={[]}
          period={manualInputs.billingPeriod}
          mode={dataEntryMethod === 'invoice' ? 'scan' : dataEntryMethod === 'csv' ? 'csv' : 'manual'}
          setMode={(m) => setDataEntryMethod(m === 'scan' ? 'invoice' : m)}
          /* The only screen with real extraction: PaddleOCR reads supplier, meter
             registers and billing period off a KESCO bill. */
          ocrSupported
          docLabel={t('elec.doc')}
          file={file}
          onFile={(f) => handleSingleUpload(f)}
          uploading={ocrLoading}
          uploadNote={ocrLoading ? (
            <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <Skeleton h={30} w="55%" />
              <Skeleton h={18} w="80%" />
              <Skeleton h={18} w="68%" />
            </div>
          ) : (
            <div style={{ marginTop: 12 }}>
              <Button
                icon={X.Upload}
                disabled={consumptionPurpose !== 'building'}
                title={consumptionPurpose !== 'building' ? t('elec.bulkOnlyBuilding') : undefined}
                onClick={() => {
                  if (consumptionPurpose !== 'building') return;
                  if (!selectedBuilding) { setBuildingValidationError(t('elec.pickBuildingFirst')); return; }
                  setShowUploadModal(true);
                }}
              >
                {t('elec.bulkUpload')}
              </Button>
            </div>
          )}
          onCalculate={handleOpenVerify}
          calcDisabled={isSaveDisabled}
          calcLabel={saving ? t('entry.calculating') : t('entry.calc')}
          reviewRows={[
            { label: t('entry.building'), value: (buildings.find(b => String(b.id) === String(selectedBuilding)) || {}).name || '—' },
            { label: t('elec.usage'), value: purposeLabel },
            selectedVehicle ? { label: t('elec.vehicle'), value: selectedVehicle } : null,
            { label: t('entry.period'), value: manualInputs.billingPeriod || '—' },
            { label: t('elec.totalConsumption'), value: `${Number(totalKwhValue).toLocaleString()} kWh` },
            /* The grid factor row the old dialog carried is deliberately gone —
               the factor is not shown on entry screens (user, 2026-07-29). */
            ...(carbonData.registrations || []).map((r, i) => ({
              label: t('elec.register', { name: r.type || i + 1 }),
              value: `${Number(r.value || 0).toLocaleString()} ${r.unit || 'kWh'}`
            })),
            { label: t('entry.document'), value: file ? file.name : t('entry.notAttached') }
          ]}
          result={{ value: tCO2e, unit: t('common.tco2e') }}
          saving={saving}
          error={flowError}
          onSave={handleFlowSave}
          saved={isSaved}
          successText={t('elec.success', { value: tCO2e, period: manualInputs.billingPeriod })}
          coverage={buildCoverageChips('scope2', coverage)}
          nextStep={nextStepFor('scope2')}
          onAddAnother={handleFlowAddAnother}
          onFinish={handleFlowFinish}
          context={
            <>
              {step === 2 && flowDocUrl && (
                <Card
                  title={t('elec.sourceDoc')}
                  sub={file ? file.name : ''}
                  bodyProps={{ style: { padding: 0 } }}
                >
                  <div style={{ borderTop: '1px solid var(--n200)', maxHeight: 420, overflow: 'auto' }}>
                    {file && file.type === 'application/pdf' ? (
                      <iframe title={t('elec.sourceDoc')} src={flowDocUrl} style={{ width: '100%', height: 420, border: 0 }} />
                    ) : (
                      <img alt={t('elec.sourceDoc')} src={flowDocUrl} style={{ width: '100%', display: 'block' }} />
                    )}
                  </div>
                </Card>
              )}
              <CategoryContext
                recordTypes={['scope2-process']}
                categoryLabel={t('elec.title')}
                facilities={buildings}
                refreshKey={ledgerKey}
                onPickSite={(id) => { setSelectedBuilding(String(id)); setStep(1); }}
              />
              <MonthLedger
                period={manualInputs.billingPeriod}
                recordTypes={['scope2-process']}
                buildings={buildings}
                refreshKey={ledgerKey}
                emptyHint={t('elec.ledgerEmpty')}
                onChanged={() => setLedgerKey(k => k + 1)}
              />
            </>
          }
        >
          {/* ── consumption purpose + vehicle ── */}
          <div className="k2-g2">
            <Field label={t('elec.purpose')}>
              <Select
                value={consumptionPurpose}
                onChange={(e) => {
                  setConsumptionPurpose(e.target.value);
                  if (e.target.value === 'building') setSelectedVehicle('');
                }}
              >
                <option value="building">{t('elec.purposeBuilding')}</option>
                <option value="ev">{t('elec.purposeEv')}</option>
                <option value="hybrid">{t('elec.purposeHybrid')}</option>
              </Select>
            </Field>

            {consumptionPurpose !== 'building' && (
              <Field label={t('elec.vehicle')}>
                <Input
                  type="text"
                  value={selectedVehicle}
                  onChange={(e) => setSelectedVehicle(e.target.value)}
                  placeholder={t('mobile.vehicleIdPlaceholder')}
                />
              </Field>
            )}
          </div>

          {/* ── figures ── */}
          <div className="k2-g2">
            <Field label={t('elec.consumption')} required>
              <UnitInput
                type="number"
                value={manualInputs.electricity}
                onChange={(e) => handleManualInputChange('electricity', e.target.value)}
                placeholder="292"
                unit="kWh"
              />
            </Field>

            <Field label={t('elec.billingPeriod')} required>
              <Input
                type="month"
                value={manualInputs.billingPeriod}
                onChange={(e) => handleManualInputChange('billingPeriod', e.target.value)}
              />
            </Field>
          </div>

          {buildingValidationError && <Callout tone="err">{buildingValidationError}</Callout>}

          {bulkProgress.processing && (
            <Callout tone="info">
              <b>{t('elec.processing')}</b>{' '}
              {t('elec.progress', { current: bulkProgress.current, total: bulkProgress.total })}
            </Callout>
          )}
        </EntryStepper>
      </div>

      {/* Bulk scanning — several bills in one go. Each is auto-saved by the
          backend and the per-bill review is suppressed, which is why this sits
          beside the stepper rather than inside it. */}
      <UploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onSingleUpload={handleSingleUpload}
        onBulkUpload={handleBulkUpload}
      />

      {showOcrFailureModal && (
        <Modal
          title={t('elec.ocrFailTitle')}
          onClose={() => setShowOcrFailureModal(false)}
          footer={
            <>
              <Button
                onClick={() => { setShowOcrFailureModal(false); setDataEntryMethod('manual'); setStep(1); }}
              >
                {t('elec.ocrFailManual')}
              </Button>
              <Button
                variant="mint"
                style={{ marginLeft: 'auto' }}
                onClick={() => { setShowOcrFailureModal(false); setFile(null); setOcrMetadata(null); }}
              >
                {t('elec.ocrFailRetry')}
              </Button>
            </>
          }
        >
          <Callout tone="warn">{t('elec.ocrFailBody')}</Callout>
        </Modal>
      )}
    </Shell>
  );

  return (
    <ErrorBoundary fallback={({ reset }) => (
      <div style={{ padding: 24 }}>
        <h3>Invoices failed to load.</h3>
        <button onClick={reset}>Retry</button>
      </div>
    )}>
      {page}
    </ErrorBoundary>
  );
};
